import type {
  Faction,
  Race,
  ResearchDomainId,
  ResearchSubject,
  ResearchTier,
  Unit,
  UnitAbility,
} from "@sins2-atlas/game-data";
import { researchTrees } from "@sins2-atlas/game-data";
import { dataset, unitsById } from "./dataset";
import { FACTION_TOKENS } from "./faction-selection";
import type { TechSelection, TreeReason } from "./tech-filters";
import { researchSubjectName } from "./tech-index";

// The Game Order model for a Research Subject (ADR 0005). The game's own tooltip sequence
// lives here rather than in JSX reading order, so the order is a tested fact: a component
// renders whatever this returns, in the order it returns it.
//
// Two deliberate divergences from the game, both from ADR 0005:
//   - Price is always present, though the game hides it behind unmet requirements. A static
//     reference has no player state, so requirements and price both always show.
//   - Nothing is dropped for being absent the way the game drops rows; a section is omitted
//     here only when the subject genuinely has no such content (no prerequisites at all).

/** One composed reverse-index line, with the roster unit it names where it names one. The
 * unit is resolved through the shipped `unlocks[].unitId` (schema v18) — never by matching
 * a name back out of the composed prose, which would break on a reworded header. The game
 * renders the target's own icon, name and description under each heading, so the whole unit
 * comes through, not just its id. */
interface UnlockEntry {
  line: string;
  unit?: Unit;
  /** The ability the line is about, where it names one. The game's tooltip describes the
   * ABILITY under an "Unlocks/Improves … Ability:" heading, not the unit carrying it, so this
   * is what the detail block renders when present — the unit stays for the link. */
  ability?: UnitAbility;
}

export type DetailSection =
  | { kind: "requirements"; prerequisiteNames: string[] }
  | { kind: "exotics" }
  | { kind: "researchTime" }
  | { kind: "price" }
  | { kind: "effects" }
  | { kind: "unlocks"; entries: UnlockEntry[] }
  | { kind: "description" };

/** Which tree owns a Research Subject: its race and domain, and the factions that can research
 * it. Built once — the same six-tree walk the name index does. */
const locationById = new Map<string, { race: Race; domain: ResearchDomainId; factions: Faction[] }>(
  researchTrees(dataset.research).flatMap(({ race, domain, tree }) =>
    tree.subjects.map(
      (subject) => [subject.id, { race, domain, factions: [...subject.factions] }] as const,
    ),
  ),
);

/** The tree the page draws: one Race, one Domain, and which of that Race's Factions the
 * tree shows. Both Factions is the UNION — the whole Race's tree, which is strictly more
 * than either Faction sees in game (ADR 0010). */
export interface Tree {
  race: Race;
  factions: Faction[];
  domain: ResearchDomainId;
  /** Why this tree, when the Faction Selection did not settle it alone. Absent when the
   * selection names exactly one Race and the tree is that Race's, which is the control
   * working as intended. The wording lives in the route; the decision lives here. */
  reason?: TreeChoice;
}

/** The reasons lib/tech-filters.ts can give for a Race, plus the one only the dataset can
 * know: a Selected Subject the Faction Selection does not reach. ADR 0006 lets the subject
 * win — a link naming a subject that then isn't shown reads as broken — but the page still
 * has to say that the Faction Picker is not what it is looking at, or the picker reads as
 * dead while the panel is open. */
type TreeChoice = TreeReason | { kind: "selectedSubject"; subjectName: string };

/** The subjects a tree draws: the ones at least one of its Factions can research. A single
 * Faction sees its own view, the pair sees the union. */
export function visibleSubjects(tree: Pick<Tree, "race" | "factions" | "domain">) {
  return dataset.research.races[tree.race][tree.domain].subjects.filter((subject) =>
    subject.factions.some((faction) => tree.factions.includes(faction)),
  );
}

/** Faction order, canonical and declared ONCE: the Faction Picker's own token table is
 * where the site fixes it (lib/faction-selection.ts), so a tree never disagrees with the
 * picker about which Faction comes first. */
const FACTION_ORDER: readonly Faction[] = [...new Set(FACTION_TOKENS.map((e) => e.faction))];

/** The tree to draw (ADR 0010 + ADR 0006). The site-wide Faction Selection chooses it —
 * lib/tech-filters.ts has already reduced the set to one Race and reported when it had to —
 * and a Selected Subject then wins over that choice, because a link naming a subject that
 * then isn't shown reads as broken.
 *
 * Two restraints on the subject's win, both older than this module's Race axis: a subject
 * both Factions share never widens the reader's own single-Faction view, and an id no tree
 * owns (a stale or hand-edited link) leaves the tree alone. */
export function treeForSelection(selection: TechSelection): Tree {
  const chosen: Tree = {
    race: selection.race,
    factions: [...selection.factions],
    domain: selection.domain,
    ...(selection.reason === undefined ? {} : { reason: selection.reason }),
  };
  const location =
    selection.subjectId === undefined ? undefined : locationById.get(selection.subjectId);
  if (location === undefined) return chosen;
  const alreadyShown =
    location.race === chosen.race &&
    location.factions.some((faction) => chosen.factions.includes(faction));
  if (alreadyShown) return { ...chosen, domain: location.domain };
  // The subject settled the tree, so whatever the selection could not settle is moot — the
  // page would otherwise draw one Race under a line naming another — and the subject itself
  // becomes the thing to report. The narrowest tree containing it is its OWN Factions.
  return {
    race: location.race,
    factions: FACTION_ORDER.filter((faction) => location.factions.includes(faction)),
    domain: location.domain,
    reason: {
      kind: "selectedSubject",
      subjectName: researchSubjectName(selection.subjectId ?? "") ?? "this subject",
    },
  };
}

/** The tier card's sections (Issue 26), in the issue's own order: Price, then the exotics
 * on the tiers that charge them, "Acquire Time", the required "<Domain> Research Points",
 * and last the "Tier Bonuses:" lines. The card's own name line is the component's; the
 * labels are the game's, shipped in `dataset.research.tierLabels`. */
export type TierDetailSection =
  | { kind: "price" }
  | { kind: "exotics" }
  | { kind: "acquireTime" }
  | { kind: "researchPoints" }
  | { kind: "bonuses" };

/** The one sentence the removed "Tier unlock costs" disclosure explained, now the tier
 * card's footer. */
export const TIER_UNLOCK_NOTE = "Each tier must be unlocked before its subjects can be researched.";

// The Tech Set action a tree cell offers (Issue 27, ADR 0009 as amended 2026-09-06). EVERY
// cell offers it — every Tier, and every one of the Race's Research Subjects. The tree draws
// the game's research and a Tech Set is the research the reader has done, so a cell that
// cannot be ticked would be the tree disagreeing with itself. Until the amendment a subject
// offered the action only when ticking it could move a number this site publishes, which was
// 120 subjects of 849 and no Civilian one at all.

/** Why a cell is marked under Fully-Teched, where the whole tree is in the set and no single
 * subject can leave it (ADR 0011: `all` is a value, not a listed membership). It names the
 * Tech control's own action rather than a state, because that is the only way out. */
export const FULL_SET_NOTE =
  "Every subject is in your tech set. Deselect all in the Tech control to pick your own.";

export function tierDetail(tier: ResearchTier): TierDetailSection[] {
  const sections: TierDetailSection[] = [{ kind: "price" }];
  if (tier.exoticPrice.length > 0) sections.push({ kind: "exotics" });
  sections.push({ kind: "acquireTime" }, { kind: "researchPoints" });
  // A header heads content: every shipped tier has at least one line (pinned in
  // extraction), but the schema allows none, and "Tier Bonuses:" over nothing would read
  // as "this tier grants nothing".
  if (tier.bonuses.length > 0) sections.push({ kind: "bonuses" });
  return sections;
}

export function subjectDetail(subject: ResearchSubject): DetailSection[] {
  const sections: DetailSection[] = [];
  // Always present: every subject sits in a tier, and the tier alone is a requirement. The
  // game shows "Research Tier Required" with just the tier when there are no prerequisite
  // subjects (Harcka Blueprint) and "Research Required" naming both when there are (PSIDAR V2).
  sections.push({
    kind: "requirements",
    // The game names the prerequisite; an unresolvable id falls back to itself rather
    // than vanishing, so a dataset gap stays visible instead of silently shrinking the row.
    prerequisiteNames: subject.prerequisites.map((id) => researchSubjectName(id) ?? id),
  });
  if (subject.exoticPrice.length > 0) sections.push({ kind: "exotics" });
  sections.push({ kind: "researchTime" });
  sections.push({ kind: "price" });
  if (subject.effects.length > 0) sections.push({ kind: "effects" });
  if (subject.unlocks.length > 0) {
    sections.push({
      kind: "unlocks",
      entries: subject.unlocks.map((entry) => {
        // The ability always lives on the unit the same line names (pinned in extraction),
        // so no global ability index is needed.
        const unit = entry.unitId === undefined ? undefined : unitsById.get(entry.unitId);
        return {
          line: entry.line,
          unit,
          ability:
            entry.abilityId === undefined
              ? undefined
              : unit?.abilities.find((ability) => ability.id === entry.abilityId),
        };
      }),
    });
  }
  if (subject.description !== undefined) sections.push({ kind: "description" });
  return sections;
}
